import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Header, Footer } from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast, Toaster } from "sonner";
import {
  AlertTriangle,
  BadgeCheck,
  CheckCircle2,
  ClipboardCheck,
  ExternalLink,
  FileText,
  IdCard,
  Loader2,
  RefreshCw,
  ShieldCheck,
  Upload,
  Users,
  XCircle,
} from "lucide-react";
import PacientesCompartidosCard from "@/components/PacientesCompartidosCard";

const DOC_META = {
  titulo_profesional: {
    label: "Título profesional",
    required: true,
    hint: "PDF del título expedido por la institución educativa.",
  },
  cedula_profesional: {
    label: "Cédula profesional",
    required: true,
    hint: "PDF de la cédula (SEP / DGP).",
  },
  cedula_especialidad: {
    label: "Cédula de especialidad",
    required: false,
    hint: "Opcional. Solo si aplica a tu especialidad.",
  },
  cv: {
    label: "Currículum profesional",
    required: false,
    hint: "Opcional. CV en PDF.",
  },
};

const emptyDireccion = {
  calle: "",
  colonia: "",
  ciudad: "",
  estado: "",
  cp: "",
};

export default function DashboardMedico() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const suscripcionFlag = searchParams.get("suscripcion");
  const sessionId = searchParams.get("session_id");

  const [catalogos, setCatalogos] = useState({ especialidades: [], estados: [] });
  const [perfil, setPerfil] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingPerfil, setSavingPerfil] = useState(false);
  const [uploadingTipo, setUploadingTipo] = useState(null);
  const [checkoutBusy, setCheckoutBusy] = useState(null);
  const [cancelBusy, setCancelBusy] = useState(false);
  const [cancelDialog, setCancelDialog] = useState(false);
  const [verifyingStripe, setVerifyingStripe] = useState(false);
  const [verifyStage, setVerifyStage] = useState("pago"); // "pago" | "activando" | "listo" | "error"
  const [verifyTimeout, setVerifyTimeout] = useState(false);

  const [form, setForm] = useState({
    nombre_completo: "",
    especialidad: "",
    especialidad_otra: "",
    cedula_profesional: "",
    cedula_especialidad: "",
    telefono: "",
    correo_contacto: "",
    cofepris_aviso: "",
    direccion: { ...emptyDireccion },
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [perfilResp, catResp] = await Promise.all([
        api.get("/medico/perfil"),
        api.get("/medico/catalogos"),
      ]);
      setPerfil(perfilResp.data);
      setCatalogos(catResp.data);
      const p = perfilResp.data;
      setForm({
        nombre_completo: p.nombre_completo || user?.nombre_completo || "",
        especialidad: p.especialidad || "",
        especialidad_otra: p.especialidad_otra || "",
        cedula_profesional: p.cedula_profesional || "",
        cedula_especialidad: p.cedula_especialidad || "",
        telefono: p.telefono || "",
        correo_contacto: p.correo_contacto || user?.email || "",
        cofepris_aviso: p.cofepris_aviso || "",
        direccion: {
          ...emptyDireccion,
          ...(p.direccion || {}),
        },
      });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    load();
  }, [load]);

    // Handle Stripe redirect verification
  useEffect(() => {
    if (!suscripcionFlag) return;
    (async () => {
      if (suscripcionFlag === "exito") {
        setVerifyingStripe(true);
        setVerifyStage("pago");
        setVerifyTimeout(false);
      }
      // Watchdog: if takes >30s show timeout UI (keeps button hidden)
      const t = setTimeout(() => setVerifyTimeout(true), 30000);
      try {
        setVerifyStage("pago");
        const { data } = await api.get("/medico/suscripcion");
        setVerifyStage("activando");
        setPerfil((prev) => (prev ? { ...prev, subscription: data, is_visible_directory: data.is_visible_directory } : prev));
        // Also refresh the whole perfil so faltantes update
        await load();
        if (suscripcionFlag === "exito" && data.status && ["active", "trialing"].includes(data.status)) {
          setVerifyStage("listo");
          toast.success("Suscripción activada. Ya apareces en el directorio si tu perfil está completo.");
        } else if (suscripcionFlag === "exito") {
          setVerifyStage("pago");
          toast.message("Verificando el pago con Stripe…");
        } else if (suscripcionFlag === "cancelada") {
          toast.message("Suscripción no completada.");
        }
      } catch (e) {
        setVerifyStage("error");
        toast.error(formatApiError(e));
      } finally {
        clearTimeout(t);
        // Give a moment for the "listo" state to be visible before hiding overlay
        setTimeout(() => setVerifyingStripe(false), 1200);
        // Clean URL
        const next = new URLSearchParams(searchParams);
        next.delete("suscripcion");
        next.delete("session_id");
        setSearchParams(next, { replace: true });
      }
    })();
  }, [suscripcionFlag, sessionId, load, searchParams, setSearchParams]);

  const reverifyStripe = async () => {
    setVerifyingStripe(true);
    setVerifyStage("pago");
    setVerifyTimeout(false);
    try {
      const { data } = await api.get("/medico/suscripcion");
      setVerifyStage("activando");
      setPerfil((prev) => (prev ? { ...prev, subscription: data, is_visible_directory: data.is_visible_directory } : prev));
      await load();
      if (data.status && ["active", "trialing"].includes(data.status)) {
        setVerifyStage("listo");
        toast.success("Suscripción confirmada.");
      } else {
        setVerifyStage("pago");
        toast.message("Aún no aparece como activa.");
      }
    } catch (e) {
      setVerifyStage("error");
      toast.error(formatApiError(e));
    } finally {
      setTimeout(() => setVerifyingStripe(false), 1200);
    }
  };

  // -------- Handlers --------

  const savePerfil = async (e) => {
    e?.preventDefault();
    const errs = validate(form);
    if (errs.length) {
      toast.error(errs[0]);
      return;
    }
    setSavingPerfil(true);
    try {
      const payload = {
        ...form,
        cedula_especialidad: (form.cedula_especialidad || "").trim(),
      };
      const { data } = await api.put("/medico/perfil", payload);
      setPerfil(data);
      toast.success("Perfil guardado.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingPerfil(false);
    }
  };

  const uploadDoc = async (tipo, file) => {
    if (!file) return;
    if (file.type !== "application/pdf") {
      toast.error("Solo se aceptan archivos PDF.");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("El archivo excede 10 MB.");
      return;
    }
    setUploadingTipo(tipo);
    try {
      const fd = new FormData();
      fd.append("tipo", tipo);
      fd.append("file", file);
      await api.post("/medico/documentos", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${DOC_META[tipo].label} cargado.`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploadingTipo(null);
    }
  };

  const previewDoc = async (docId) => {
    try {
      const { data } = await api.post(`/medico/documentos/${docId}/url-firmada`);
      const publicUrl = `${process.env.REACT_APP_BACKEND_URL}/api/medico/documentos/descarga/${data.token}`;
      window.open(publicUrl, "_blank", "noopener,noreferrer");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteDoc = async (docId) => {
    try {
      await api.delete(`/medico/documentos/${docId}`);
      toast.success("Documento eliminado.");
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const startCheckout = async (plan) => {
    // Defensive guard: refuse to start a checkout if we already show an active
    // subscription (server also enforces this with a 409, but this avoids the
    // "click twice" race and the spinner-on-active-sub bug).
    const currentSub = perfil?.subscription || {};
    if (["active", "trialing", "comped_seed"].includes(currentSub.status)) {
      toast.message("Ya tienes una suscripción activa.");
      return;
    }
    setCheckoutBusy(plan);
    try {
      const { data } = await api.post("/medico/suscripcion/checkout", {
        plan,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err));
      setCheckoutBusy(null);
    }
  };

  const cancelSuscripcion = async () => {
    setCancelBusy(true);
    try {
      await api.post("/medico/suscripcion/cancelar");
      toast.success("La suscripción se cancelará al final del periodo actual.");
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setCancelBusy(false);
      setCancelDialog(false);
    }
  };

  if (loading || !perfil) {
    return (
      <Shell>
        <div className="text-sm text-muted-foreground flex items-center gap-2">
          <Loader2 size={14} className="animate-spin" /> Cargando panel médico…
        </div>
      </Shell>
    );
  }

  const sub = perfil.subscription || {};
  const isVisible = !!perfil.is_visible_directory;
  const isComped = sub.status === "comped_seed";

  return (
    <Shell>
      {/* Hero */}
      <section className="rounded-xl border border-border bg-white p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-widest text-[#2563EB] flex items-center gap-2">
            <span>Panel del médico</span>
            {user?.role === "admin" && (
              <span
                className="inline-flex items-center gap-1 rounded-full bg-[#0F2942] text-white px-2 py-0.5 text-[10px] tracking-widest normal-case font-medium"
                data-testid="admin-mode-chip"
              >
                Modo administrador
              </span>
            )}
          </div>
          <h1 className="font-display text-3xl font-semibold text-[#0F2942] tracking-tight mt-1">
            Hola, {(user?.nombre_completo || "").split(" ").slice(-1)[0] || "médico"}
          </h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-md">
            Completa tu perfil profesional, sube tus documentos y activa tu suscripción para
            aparecer en el Directorio de Especialistas.
          </p>
        </div>
        <Link to="/directorio" target="_blank" rel="noopener noreferrer">
          <Button variant="outline" size="sm" data-testid="medico-view-directorio">
            <Users size={14} /> Ver directorio <ExternalLink size={12} />
          </Button>
        </Link>
      </section>

      {/* Visibility indicator */}
      <VisibilityBanner perfil={perfil} isVisible={isVisible} />

      {/* Stripe redirect verification stages (only when returning from Stripe or explicit re-verify) */}
      {verifyingStripe && (
        <section
          className="mt-4 rounded-xl border border-blue-200 bg-blue-50/60 p-5"
          data-testid="medico-verify-stripe-panel"
        >
          <div className="text-[11px] uppercase tracking-widest text-blue-900 font-semibold mb-3">
            Verificando tu pago
          </div>
          <ol className="space-y-2.5" data-testid="medico-verify-stages">
            <VerifyStage
              testid="stage-pago"
              label="Confirmando tu pago con Stripe…"
              doneLabel="Pago confirmado"
              done={["activando", "listo"].includes(verifyStage)}
              active={verifyStage === "pago"}
            />
            <VerifyStage
              testid="stage-activando"
              label="Activando tu suscripción…"
              doneLabel="Suscripción activada"
              done={verifyStage === "listo"}
              active={verifyStage === "activando"}
            />
            <VerifyStage
              testid="stage-listo"
              label="Listo — aparecerás en el directorio si tu perfil está completo."
              doneLabel="Listo — aparecerás en el directorio si tu perfil está completo."
              done={verifyStage === "listo"}
              active={false}
            />
          </ol>
          {verifyTimeout && verifyStage !== "listo" && (
            <div className="mt-3 rounded-md border border-amber-300 bg-amber-50 text-amber-900 p-3 text-sm flex items-start gap-2" data-testid="medico-verify-timeout">
              <AlertTriangle size={16} className="shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="font-semibold">Esto está tardando más de lo normal.</div>
                <div className="text-xs mt-0.5">Tu pago está seguro. Puedes verificar de nuevo.</div>
              </div>
              <Button variant="outline" size="sm" onClick={reverifyStripe} data-testid="medico-verify-again-btn">
                <RefreshCw size={12} /> Verificar de nuevo
              </Button>
            </div>
          )}
        </section>
      )}

      {/* Pacientes que compartieron su expediente (Fase 5.9) */}
      <PacientesCompartidosCard />

      {/* PERFIL PROFESIONAL */}
      <section
        className="mt-6 rounded-xl border border-border bg-white p-6 sm:p-8"
        data-testid="medico-perfil-section"
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-widest text-[#2563EB]">
              Sección 1 · Perfil profesional
            </div>
            <h2 className="font-display text-xl font-semibold text-[#0F2942] mt-1">
              Datos que aparecen en el directorio
            </h2>
          </div>
          <IdCard className="text-[#0F2942]/40" size={22} />
        </div>

        <form onSubmit={savePerfil} className="mt-5 grid gap-5">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Nombre completo" required>
              <Input
                value={form.nombre_completo}
                onChange={(e) => setForm({ ...form, nombre_completo: e.target.value })}
                placeholder="Dr(a). Nombre Apellido Apellido"
                data-testid="medico-input-nombre"
                autoComplete="name"
              />
            </Field>

            <Field label="Correo de contacto (público)" required>
              <Input
                type="email"
                value={form.correo_contacto}
                onChange={(e) => setForm({ ...form, correo_contacto: e.target.value })}
                placeholder="consulta@tu-dominio.mx"
                data-testid="medico-input-correo"
                autoComplete="email"
              />
            </Field>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Especialidad" required>
              <Select
                value={form.especialidad}
                onValueChange={(v) => setForm({ ...form, especialidad: v })}
              >
                <SelectTrigger data-testid="medico-select-especialidad">
                  <SelectValue placeholder="Selecciona una especialidad" />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {(catalogos.especialidades || []).map((esp) => (
                    <SelectItem key={esp} value={esp} data-testid={`esp-opt-${slug(esp)}`}>
                      {esp}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            {form.especialidad === "Otra" ? (
              <Field label="Especifica tu especialidad" required>
                <Input
                  value={form.especialidad_otra}
                  onChange={(e) => setForm({ ...form, especialidad_otra: e.target.value })}
                  placeholder="Ej. Medicina del deporte"
                  data-testid="medico-input-esp-otra"
                />
              </Field>
            ) : (
              <Field
                label="Teléfono de consultorio (10 dígitos)"
                required
              >
                <Input
                  value={form.telefono}
                  onChange={(e) =>
                    setForm({ ...form, telefono: e.target.value.replace(/\D/g, "").slice(0, 10) })
                  }
                  placeholder="5551234567"
                  data-testid="medico-input-telefono"
                  inputMode="numeric"
                />
              </Field>
            )}
          </div>

          {form.especialidad === "Otra" && (
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Teléfono de consultorio (10 dígitos)" required>
                <Input
                  value={form.telefono}
                  onChange={(e) =>
                    setForm({ ...form, telefono: e.target.value.replace(/\D/g, "").slice(0, 10) })
                  }
                  placeholder="5551234567"
                  data-testid="medico-input-telefono"
                  inputMode="numeric"
                />
              </Field>
              <div />
            </div>
          )}

          <div className="grid sm:grid-cols-2 gap-4">
            <Field
              label="Cédula profesional (7-8 dígitos)"
              required
              hint="Se muestra en el directorio como referencia de ejercicio profesional."
            >
              <Input
                value={form.cedula_profesional}
                onChange={(e) =>
                  setForm({
                    ...form,
                    cedula_profesional: e.target.value.replace(/\D/g, "").slice(0, 8),
                  })
                }
                placeholder="12345678"
                data-testid="medico-input-cedula"
                inputMode="numeric"
              />
            </Field>

            <Field
              label="Cédula de especialidad (opcional)"
              hint="7 u 8 dígitos, solo si aplica."
            >
              <Input
                value={form.cedula_especialidad}
                onChange={(e) =>
                  setForm({
                    ...form,
                    cedula_especialidad: e.target.value.replace(/\D/g, "").slice(0, 8),
                  })
                }
                placeholder="Opcional"
                data-testid="medico-input-cedula-esp"
                inputMode="numeric"
              />
            </Field>
          </div>

          <Field
            label="Folio de aviso de funcionamiento COFEPRIS"
            required
            hint="Aviso legal del consultorio ante COFEPRIS."
          >
            <Input
              value={form.cofepris_aviso}
              onChange={(e) => setForm({ ...form, cofepris_aviso: e.target.value })}
              placeholder="Ej. COFEPRIS-224-2024-XXX-01"
              data-testid="medico-input-cofepris"
            />
          </Field>

          {/* Dirección */}
          <div className="border-t border-border pt-5">
            <div className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
              Dirección del consultorio
            </div>

            <div className="mt-3 grid sm:grid-cols-2 gap-4">
              <Field label="Calle y número" required>
                <Input
                  value={form.direccion.calle}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      direccion: { ...form.direccion, calle: e.target.value },
                    })
                  }
                  placeholder="Av. Insurgentes Sur 1234, Piso 5"
                  data-testid="medico-input-calle"
                />
              </Field>
              <Field label="Colonia" required>
                <Input
                  value={form.direccion.colonia}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      direccion: { ...form.direccion, colonia: e.target.value },
                    })
                  }
                  placeholder="Del Valle"
                  data-testid="medico-input-colonia"
                />
              </Field>
            </div>

            <div className="mt-4 grid sm:grid-cols-3 gap-4">
              <Field label="Ciudad / municipio" required>
                <Input
                  value={form.direccion.ciudad}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      direccion: { ...form.direccion, ciudad: e.target.value },
                    })
                  }
                  placeholder="Benito Juárez"
                  data-testid="medico-input-ciudad"
                />
              </Field>
              <Field label="Estado" required>
                <Select
                  value={form.direccion.estado}
                  onValueChange={(v) =>
                    setForm({ ...form, direccion: { ...form.direccion, estado: v } })
                  }
                >
                  <SelectTrigger data-testid="medico-select-estado">
                    <SelectValue placeholder="Selecciona" />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    {(catalogos.estados || []).map((es) => (
                      <SelectItem key={es} value={es} data-testid={`estado-opt-${slug(es)}`}>
                        {es}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Código postal (5 dígitos)" required>
                <Input
                  value={form.direccion.cp}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      direccion: {
                        ...form.direccion,
                        cp: e.target.value.replace(/\D/g, "").slice(0, 5),
                      },
                    })
                  }
                  placeholder="03100"
                  data-testid="medico-input-cp"
                  inputMode="numeric"
                />
              </Field>
            </div>
          </div>

          <div className="flex items-center justify-end pt-2">
            <Button
              type="submit"
              disabled={savingPerfil}
              className="bg-[#2563EB] hover:bg-[#1e4fd0] text-white"
              data-testid="medico-save-perfil-btn"
            >
              {savingPerfil ? (
                <>
                  <Loader2 size={14} className="animate-spin" /> Guardando…
                </>
              ) : (
                <>
                  <ClipboardCheck size={14} /> Guardar perfil
                </>
              )}
            </Button>
          </div>
        </form>
      </section>

      {/* DOCUMENTOS */}
      <section
        className="mt-6 rounded-xl border border-border bg-white p-6 sm:p-8"
        data-testid="medico-documentos-section"
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-widest text-[#2563EB]">
              Sección 2 · Documentos profesionales
            </div>
            <h2 className="font-display text-xl font-semibold text-[#0F2942] mt-1">
              Cumplimiento COFEPRIS (PDF, máx 10 MB)
            </h2>
            <p className="text-sm text-muted-foreground mt-1 max-w-lg">
              Estos documentos son privados. Se almacenan cifrados y jamás se exponen en el
              directorio público. Solo tú puedes descargarlos con URL firmada de 5 min.
            </p>
          </div>
          <FileText className="text-[#0F2942]/40" size={22} />
        </div>

        <div className="mt-5 grid sm:grid-cols-2 gap-3">
          {Object.keys(DOC_META).map((tipo) => (
            <DocRow
              key={tipo}
              tipo={tipo}
              doc={perfil.documentos?.[tipo]}
              uploading={uploadingTipo === tipo}
              onUpload={(f) => uploadDoc(tipo, f)}
              onPreview={previewDoc}
              onDelete={deleteDoc}
              disabled={isComped}
            />
          ))}
        </div>

        {isComped && (
          <div className="mt-4 rounded-md border border-blue-200 bg-blue-50 text-blue-900 p-3 text-xs">
            Esta es una cuenta demo comped. La carga real de documentos está deshabilitada para
            este perfil.
          </div>
        )}
      </section>

      {/* SUSCRIPCIÓN */}
      <section
        className="mt-6 rounded-xl border border-border bg-white p-6 sm:p-8"
        data-testid="medico-suscripcion-section"
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-widest text-[#2563EB]">
              Sección 3 · Suscripción al directorio
            </div>
            <h2 className="font-display text-xl font-semibold text-[#0F2942] mt-1">
              {sub.status === "active" || sub.status === "trialing" || isComped
                ? "Tu suscripción está activa"
                : "Activa tu suscripción para aparecer en el directorio"}
            </h2>
          </div>
          <ShieldCheck className="text-[#0F2942]/40" size={22} />
        </div>

        {isComped ? (
          <div className="mt-5 rounded-md border border-emerald-200 bg-emerald-50 text-emerald-900 p-4 flex items-start gap-3">
            <CheckCircle2 size={18} className="shrink-0 mt-0.5 text-emerald-600" />
            <div>
              <div className="text-sm font-semibold">Suscripción demo comped</div>
              <div className="text-xs mt-0.5">
                Este perfil demo no requiere pago. En cuentas reales, la suscripción vence y se
                renueva vía Stripe.
              </div>
            </div>
          </div>
        ) : sub.status === "active" || sub.status === "trialing" ? (
          <div className="mt-5 space-y-3">
            <div className="rounded-md border border-emerald-200 bg-emerald-50 p-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold text-emerald-900">
                    Plan {sub.plan === "anual" ? "anual" : "mensual"} · activo
                  </div>
                  <div className="text-xs text-emerald-900 mt-0.5">
                    {sub.cancel_at_period_end
                      ? "Se cancelará al finalizar el periodo actual."
                      : "Renovación automática."}{" "}
                    {sub.current_period_end && (
                      <>
                        {sub.cancel_at_period_end ? "Termina el " : "Próxima renovación: "}
                        {new Date(sub.current_period_end).toLocaleDateString("es-MX", {
                          day: "2-digit",
                          month: "long",
                          year: "numeric",
                        })}
                        .
                      </>
                    )}
                  </div>
                </div>
                {!sub.cancel_at_period_end && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCancelDialog(true)}
                    data-testid="medico-cancel-suscripcion-btn"
                  >
                    Cancelar
                  </Button>
                )}
              </div>
            </div>

            {sub.cancel_at_period_end && (
              <div className="rounded-md border border-amber-200 bg-amber-50 text-amber-900 p-3 text-xs flex items-start gap-2">
                <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                <div>
                  La suscripción sigue vigente hasta la fecha indicada. Después, dejarás de
                  aparecer en el directorio automáticamente.
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="mt-5 grid sm:grid-cols-2 gap-4">
            <PlanCard
              plan="mensual"
              price="$69"
              period="/ mes"
              copy="Facturación mensual, cancela cuando quieras."
              busy={checkoutBusy === "mensual"}
              disabled={!!checkoutBusy || verifyingStripe}
              onSelect={() => startCheckout("mensual")}
              testid="medico-plan-mensual-btn"
            />
            <PlanCard
              plan="anual"
              price="$699"
              period="/ año"
              copy="Ahorra 2 meses vs. plan mensual. Facturación anual."
              highlight
              busy={checkoutBusy === "anual"}
              disabled={!!checkoutBusy || verifyingStripe}
              onSelect={() => startCheckout("anual")}
              testid="medico-plan-anual-btn"
            />
          </div>
        )}
      </section>

      {/* Cancel dialog */}
      <Dialog open={cancelDialog} onOpenChange={setCancelDialog}>
        <DialogContent data-testid="medico-cancel-dialog">
          <DialogHeader>
            <DialogTitle>Cancelar suscripción</DialogTitle>
            <DialogDescription>
              Tu perfil seguirá visible en el directorio hasta la fecha de renovación actual.
              Después, se ocultará automáticamente. ¿Deseas continuar?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCancelDialog(false)}
              data-testid="medico-cancel-dismiss"
            >
              Mantener suscripción
            </Button>
            <Button
              onClick={cancelSuscripcion}
              disabled={cancelBusy}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="medico-cancel-confirm"
            >
              {cancelBusy ? (
                <>
                  <Loader2 size={14} className="animate-spin" /> Procesando…
                </>
              ) : (
                "Cancelar al fin del periodo"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Shell>
  );
}

// -------------------- Subcomponents --------------------

function VisibilityBanner({ perfil, isVisible }) {
  const sub = perfil?.subscription || {};
  if (isVisible) {
    const override = !!sub.admin_visible_override;
    return (
      <div
        className={`mt-6 rounded-xl border p-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between ${
          override
            ? "border-indigo-200 bg-indigo-50"
            : "border-emerald-200 bg-emerald-50"
        }`}
        data-testid="medico-visible-banner"
      >
        <div className="flex items-start gap-3">
          <CheckCircle2
            className={`shrink-0 mt-0.5 ${override ? "text-indigo-600" : "text-emerald-600"}`}
            size={22}
          />
          <div>
            <div
              className={`text-sm font-semibold ${
                override ? "text-indigo-900" : "text-emerald-900"
              }`}
              data-testid={override ? "medico-visible-override" : "medico-visible-normal"}
            >
              {override
                ? "Visible por autorización del administrador"
                : "Visible en el Directorio de Especialistas"}
            </div>
            <div
              className={`text-xs mt-0.5 ${override ? "text-indigo-800" : "text-emerald-800"}`}
            >
              {override
                ? "Un administrador de Siempre Salud te dio visibilidad manual en el directorio, aunque tu perfil no esté completo o tu suscripción no esté activa."
                : "Las personas usuarias que la IA derive verán tu ficha con contacto directo. Puedes editar tu perfil en cualquier momento."}
            </div>
          </div>
        </div>
        <Link to={perfil?.id ? `/directorio/${perfil.id}` : "/directorio"} target="_blank">
          <Button variant="outline" size="sm" data-testid="medico-preview-ficha-btn">
            Ver mi ficha <ExternalLink size={12} />
          </Button>
        </Link>
      </div>
    );
  }

  // Admin override: perfil ok + suscripción activa pero admin lo ocultó.
  if (perfil?.admin_hidden) {
    return (
      <div
        className="mt-6 rounded-xl border border-amber-300 bg-amber-50 p-4 flex items-start gap-3"
        data-testid="medico-admin-hidden-banner"
      >
        <AlertTriangle className="text-amber-600 shrink-0 mt-0.5" size={22} />
        <div>
          <div className="text-sm font-semibold text-amber-900">
            Ocultado por administrador — contacta soporte
          </div>
          <div className="text-xs text-amber-800 mt-0.5">
            Un administrador de Siempre Salud ocultó temporalmente tu ficha del directorio.
            Escríbenos a <span className="font-mono">soporte@siempresalud.mx</span> para más
            información.
          </div>
        </div>
      </div>
    );
  }

  const faltantes = perfil?.faltantes || [];
  const needsSub = !["active", "trialing", "comped_seed"].includes(sub.status);
  return (
    <div
      className="mt-6 rounded-xl border border-amber-300 bg-amber-50 p-4"
      data-testid="medico-noVisible-banner"
    >
      <div className="flex items-start gap-3">
        <AlertTriangle className="text-amber-600 shrink-0 mt-0.5" size={22} />
        <div className="flex-1">
          <div className="text-sm font-semibold text-amber-900">
            No visible en el directorio
          </div>
          <div className="text-xs text-amber-800 mt-0.5">
            Para aparecer necesitas completar los siguientes puntos:
          </div>
          <ul className="mt-2 space-y-1 text-xs text-amber-900" data-testid="medico-faltantes-list">
            {faltantes.map((f) => (
              <li key={f} className="flex items-center gap-1.5">
                <XCircle size={12} className="text-amber-700" /> {f}
              </li>
            ))}
            {needsSub && (
              <li className="flex items-center gap-1.5" data-testid="falta-suscripcion">
                <XCircle size={12} className="text-amber-700" /> Activa tu suscripción al
                directorio ($69 mensual o $699 anual).
              </li>
            )}
          </ul>
        </div>
      </div>
    </div>
  );
}

function DocRow({ tipo, doc, uploading, onUpload, onPreview, onDelete, disabled }) {
  const meta = DOC_META[tipo];
  const fileRef = useRef(null);
  return (
    <div
      className={`rounded-lg border ${doc ? "border-emerald-200 bg-emerald-50/40" : "border-border bg-white"} p-4 flex flex-col`}
      data-testid={`doc-row-${tipo}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <div className="text-sm font-semibold text-[#0F2942]">{meta.label}</div>
            {meta.required && (
              <span className="text-[10px] uppercase tracking-widest font-semibold text-amber-700">
                Requerido
              </span>
            )}
          </div>
          <div className="text-xs text-muted-foreground mt-0.5">{meta.hint}</div>
        </div>
        {doc && (
          <span
            className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-white px-2 py-0.5 text-[10px] font-semibold text-emerald-800"
            data-testid={`doc-badge-${tipo}`}
          >
            <BadgeCheck size={12} /> Subido
          </span>
        )}
      </div>

      <input
        ref={fileRef}
        type="file"
        accept="application/pdf"
        className="hidden"
        onChange={(e) => onUpload(e.target.files?.[0])}
        data-testid={`doc-input-${tipo}`}
      />

      <div className="mt-3 flex items-center gap-2 flex-wrap">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => fileRef.current?.click()}
          disabled={uploading || disabled}
          data-testid={`doc-upload-btn-${tipo}`}
        >
          {uploading ? (
            <>
              <Loader2 size={12} className="animate-spin" /> Subiendo…
            </>
          ) : (
            <>
              <Upload size={12} /> {doc ? "Reemplazar PDF" : "Subir PDF"}
            </>
          )}
        </Button>
        {doc && (
          <>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onPreview(doc.id)}
              data-testid={`doc-preview-btn-${tipo}`}
            >
              Previsualizar
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onDelete(doc.id)}
              disabled={disabled}
              data-testid={`doc-delete-btn-${tipo}`}
            >
              Eliminar
            </Button>
          </>
        )}
      </div>

      {doc && (
        <div className="mt-2 text-[11px] text-muted-foreground">
          Cargado el {new Date(doc.uploaded_at).toLocaleString("es-MX")} · PDF ·{" "}
          {humanSize(doc.size)}
        </div>
      )}
    </div>
  );
}

function PlanCard({ plan, price, period, copy, highlight, busy, disabled, onSelect, testid }) {
  return (
    <div
      className={`rounded-xl border p-5 flex flex-col justify-between ${
        highlight ? "border-[#2563EB] bg-[#2563EB]/5" : "border-border bg-white"
      }`}
    >
      <div>
        <div className="text-[11px] font-semibold uppercase tracking-widest text-[#2563EB]">
          Plan {plan}
        </div>
        <div className="mt-2 flex items-baseline gap-1">
          <span className="font-display text-3xl font-semibold text-[#0F2942]">{price}</span>
          <span className="text-sm text-muted-foreground">MXN {period}</span>
        </div>
        <p className="text-xs text-muted-foreground mt-2 leading-relaxed">{copy}</p>
        <ul className="mt-3 space-y-1 text-xs text-foreground">
          <li className="flex items-center gap-1.5">
            <CheckCircle2 size={12} className="text-emerald-600" /> Visibilidad completa en el directorio
          </li>
          <li className="flex items-center gap-1.5">
            <CheckCircle2 size={12} className="text-emerald-600" /> Contacto directo por teléfono y correo
          </li>
          <li className="flex items-center gap-1.5">
            <CheckCircle2 size={12} className="text-emerald-600" /> Cancela cuando quieras
          </li>
        </ul>
      </div>
      <Button
        type="button"
        onClick={onSelect}
        disabled={busy || disabled}
        className={`mt-5 ${
          highlight
            ? "bg-[#2563EB] hover:bg-[#1e4fd0] text-white"
            : "bg-[#0F2942] hover:bg-[#123553] text-white"
        }`}
        data-testid={testid}
      >
        {busy ? (
          <>
            <Loader2 size={14} className="animate-spin" /> Redirigiendo a Stripe…
          </>
        ) : (
          <>Contratar plan {plan}</>
        )}
      </Button>
    </div>
  );
}

function Field({ label, hint, required, children }) {
  return (
    <div>
      <Label className="text-xs font-medium text-foreground">
        {label}
        {required && <span className="text-amber-700"> *</span>}
      </Label>
      <div className="mt-1.5">{children}</div>
      {hint && <div className="text-[11px] text-muted-foreground mt-1">{hint}</div>}
    </div>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />
      <main className="flex-1 max-w-4xl w-full mx-auto px-6 py-10">{children}</main>
      <Footer />
      <Toaster position="top-right" richColors />
    </div>
  );
}

// -------------------- Utils --------------------

function validate(form) {
  const errs = [];
  if (!form.nombre_completo || form.nombre_completo.trim().length < 2)
    errs.push("El nombre completo es requerido.");
  if (!form.especialidad) errs.push("Selecciona una especialidad.");
  if (form.especialidad === "Otra" && !form.especialidad_otra.trim())
    errs.push("Describe la especialidad en el campo 'Otra'.");
  if (!/^\d{7,8}$/.test(form.cedula_profesional))
    errs.push("Cédula profesional debe tener 7-8 dígitos.");
  if (form.cedula_especialidad && !/^\d{7,8}$/.test(form.cedula_especialidad))
    errs.push("Cédula de especialidad debe tener 7-8 dígitos.");
  if (!/^\d{10}$/.test(form.telefono)) errs.push("Teléfono debe tener 10 dígitos.");
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.correo_contacto))
    errs.push("Correo de contacto no válido.");
  if (!form.cofepris_aviso.trim()) errs.push("Folio COFEPRIS requerido.");
  const d = form.direccion;
  if (!d.calle.trim()) errs.push("Calle requerida.");
  if (!d.colonia.trim()) errs.push("Colonia requerida.");
  if (!d.ciudad.trim()) errs.push("Ciudad requerida.");
  if (!d.estado) errs.push("Selecciona un estado.");
  if (!/^\d{5}$/.test(d.cp)) errs.push("Código postal debe tener 5 dígitos.");
  return errs;
}

function humanSize(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

function slug(s) {
  return (s || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

function VerifyStage({ label, doneLabel, done, active, testid }) {
  return (
    <li className="flex items-start gap-3" data-testid={testid}>
      <span className={
        "mt-0.5 grid place-items-center w-5 h-5 rounded-full text-[11px] font-medium shrink-0 " +
        (done
          ? "bg-emerald-100 text-emerald-700 border border-emerald-300"
          : active
          ? "bg-blue-100 text-blue-700 border border-blue-300"
          : "bg-neutral-100 text-neutral-400 border border-neutral-200")
      }>
        {done ? <CheckCircle2 size={12} /> : active ? <Loader2 size={12} className="animate-spin" /> : "•"}
      </span>
      <div className="flex-1">
        <div className={
          "text-sm " +
          (done ? "text-emerald-800" : active ? "text-[#0F2942] font-medium" : "text-muted-foreground")
        }>
          {done ? doneLabel : label}
        </div>
      </div>
    </li>
  );
}

